import { Injectable } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router, UrlTree } from '@angular/router';
import {
  function as F,
  io,
  option as O,
  reader as R,
  readonlyArray as RA,
  task as T,
} from 'fp-ts';
import { comparer, runInAction, toJS } from 'mobx';
import { action, observable } from 'mobx-angular';
import { makeObservable } from '../utils/mobx';
import { BaseStore } from './base.store';

type HistoryItem = Readonly<{
  url: string;
  extras: NavigationExtras;
}>;

const back = F.pipe(
  R.ask<{
    navigationStore: NavigationStore;
    url: any[] | string | UrlTree;
    extras?: NavigationExtras;
    router: Router;
  }>(),
  R.map(({ url, extras, navigationStore, router }) =>
    F.pipe(
      io.fromIO(
        F.pipe(
          runInAction(() => toJS(navigationStore.history)),
          RA.tail,
          history =>
            io.fromIO(
              action(() => {
                navigationStore.changeHistory(
                  F.pipe(
                    history,
                    O.getOrElse((): readonly HistoryItem[] => []),
                  ),
                );

                return F.pipe(history, O.chain(RA.head));
              }),
            ),
        ),
      ),
      T.fromIO,
      T.chain(
        F.flow(
          O.fold(
            (): T.Task<boolean> => () =>
              Array.isArray(url)
                ? router.navigate(url, extras)
                : router.navigateByUrl(url, extras),
            ({ url, extras }): T.Task<boolean> =>
              () =>
                router.navigateByUrl(url, extras),
          ),
        ),
      ),
    ),
  ),
);

@Injectable({
  providedIn: 'root',
})
export class NavigationStore extends BaseStore {
  @observable
  history: ReadonlyArray<HistoryItem> = [
    {
      url: this.router.url,
      extras: toJS(this.router.getCurrentNavigation()?.extras ?? {}),
    },
  ];

  /**
   * Navigation back using custom history.
   *
   * History does not include items with extras.state.replaceNav === true,
   *   and extras.replaceUrl === true.
   *
   * @param fallbackURL - fallback URL will be used when no URLs in history.
   * @param fallbackExtras - extra options to fallbackURL.
   */
  back = (
    fallbackURL: string | UrlTree | any[] = ['..'],
    fallbackExtras?: NavigationExtras,
  ) =>
    back({
      navigationStore: this,
      router: this.router,
      url: fallbackURL,
      extras: fallbackExtras,
    })();

  @action
  changeHistory = (history: readonly HistoryItem[]) => {
    this.history = history;
  };

  constructor(private router: Router) {
    super();
    makeObservable(this);

    router.events.pipe(this.takeUntilDestroy()).subscribe(event => {
      const extras = router.getCurrentNavigation()?.extras || {};
      if (!(event instanceof NavigationEnd)) {
        return;
      }

      runInAction(() => {
        const item = { extras: toJS(extras), url: event.urlAfterRedirects };
        if (this.history.length > 0 && comparer.structural(this.history[0], item)) {
          return;
        }

        this.history = [
          item,
          ...this.history.slice(
            item.extras.state?.replaceNav || item.extras.replaceUrl ? 1 : 0,
            99,
          ),
        ];
      });
    });
  }
}
